import { getAvailableDeliveryMethods } from "api/get-available-delivery-methods.api";
import PAButton from "component/design-system/buttons/PAButton";
import PostalCodeInput from "component/design-system/forms/PostalCodeInput";
import { FormContainer } from "component/design-system/shared-styles/StyledForm";
import PAText, { FONT_SIZE } from "component/design-system/typography/PAText/PAText";
import Form from "component/formsy-components/form";
import { DELIVERY_METHODS } from "constant/action-constants";
import globalStyles from "modules/css/globalStyle.module.scss";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import {
  FC, memo, useMemo, useState,
} from "react";
import HideIf from "util/HideIf";
import LandingPageBanner from "../../component/landing-page/LandingPageBanner";
import PageContainer from "../../modules/components/PageContainer";
import ContentIdsComponent from "../../modules/static-pages/content-ids/contentIds";
import { getRelativeImagePath } from "../../util/helper/getRelativeImagePath";
import ChatReturn from "../returns/ChatReturn";
import LinkCard from "../shipping-information/link-card.component";
import styles from "./rapid-delivery.module.scss";

// Define types for better type safety
interface DeliveryStep {
  title: string;
}

interface ContentId {
  url: string;
  uiDetails?: {
    content: any;
  };
}

interface ResponseData {
  landingPage: {
    headers: {
      h1: string;
    };
    metaTitle: string;
    metaDescription: string;
    metaKeywords: string;
    responsiveBannerMedia: {
      squareImage: {
        url: string;
      };
      desktop: {
        url: string;
      };
    };
    links: {
      contentIds: ContentId[];
    };
  };
}

interface RapidDeliveryProps {
  actualResponse?: ResponseData;
}

// Move constant data outside component to prevent recreation on each render
const RAPID_DELIVERY_PROCEDURE: DeliveryStep[] = [
  {
    title: "rapid_delivery_step_1",
  },
  {
    title: "rapid_delivery_step_2",
  },
  {
    title: "rapid_delivery_step_3",
  },
];

// Extract smaller components for better organization
const DeliverySteps: FC<{ steps: DeliveryStep[] }> = ({ steps }) => {
  const { t } = useTranslation("static-pages");

  return (
    <div className={`${styles.steps} d-flex flex-wrap justify-content-around`}>
      {
        steps.map((step, index) => (
          <div
            key={`step-${index}`}
            className={`col-sm-3 p-lr-0-xs col-xs-12 justify-content-start m-b-10 ${styles.stepper}`}
          >
            <div className={styles.stepper_index}>{index + 1}</div>
            <div className="d-flex flex-column align-items-center" style={{ fontSize: 16 }}>
              {t(step.title)}
            </div>
          </div>
        ))
      }
    </div>
  );
};

const MetaTags: FC<{ title: string; description: string; keywords: string }> = ({
  title,
  description,
  keywords,
}) => (
  <Head>
    <title>{title}</title>
    <meta name="description" content={description} />
    <meta name="robots" content="all" />
    <meta name="keywords" content={keywords} />
  </Head>
);

function RapidDelivery({ actualResponse }: RapidDeliveryProps) {
  const { t } = useTranslation("static-pages");
  const [validPostalCode, setValidPostalCode] = useState(false);
  const [isRapidDeliveryAvailable, setIsRapidDeliveryAvailable] = useState<boolean | null>(null);
  const [postalCodeError, setPostalCodeError] = useState("");

  const response = actualResponse?.landingPage || null;

  const {
    headers,
    metaTitle,
    metaDescription,
    metaKeywords,
    responsiveBannerMedia,
  } = response;

  // Find FAQ content with useMemo to avoid recalculation on re-renders
  const faqs = useMemo(() => {
    const contentIds = response.links.contentIds || [];
    return contentIds.find((content: ContentId) => ["rapid-delivery-faq", "fr-rapid-delivery-faq"].includes(content.url));
  }, [response.links.contentIds]);

  // Prepare banner images paths
  const bannerImages = {
    mobileImage: getRelativeImagePath(`/cms/${responsiveBannerMedia.squareImage.url}`),
    desktopImage: getRelativeImagePath(`/cms/${responsiveBannerMedia.desktop.url}`),
  };

  const onValidSubmit = async (model) => {
    const { data } = await getAvailableDeliveryMethods(model.postalCode);
    if (data.success) {
      setIsRapidDeliveryAvailable(data.deliveryMethods.includes(DELIVERY_METHODS.DELIVERY));
      setPostalCodeError("");
    } else if (data.message === "POSTAL_CODE_NOT_SUPPORTED") {
      setIsRapidDeliveryAvailable(null);
      setPostalCodeError("Postal Code is not supported");
    }
  };

  return (
    <PageContainer>
      <MetaTags
        title={metaTitle}
        description={metaDescription}
        keywords={metaKeywords}
      />

      <LandingPageBanner
        className="m-t-10"
        style={{ height: "auto" }}
        bannerImages={bannerImages}
        width={1340}
        height={410}
        applyStyle={false}
        alt={t("rapid-delivery")}
      />

      <PAText bold component="h1" className="text-center p-t-30" color="black">
        {headers.h1}
      </PAText>

      <div className="d-flex flex-column justify-content-between flex-wrap m-t-20 align-items-center">
        <PAText component="h4" className={`text-center p-t-10 ${styles.centered_text}`} color="black">
          {t("rapidDelivery_heading_1")}
        </PAText>
        <PAText component="h4" className="text-center p-t-5" color="black">
          {t("rapidDelivery_heading_2")}
        </PAText>
      </div>

      <section className="d-flex flex-column flex-wrap m-t-20 p-t-20">
        <div className={styles.titleBanner}>
          <PAText bold component="h2" className="text-center p-b-20" color="black">
            {t("Delivery Area")}
          </PAText>
        </div>

        <div className={`${styles.delivery_area}`}>
          <PAText
            component="h4"
            className={`p-t-10 p-b-10 ${styles.centered_text}`}
            color="black"
          >
            {t("delivery_area_heading")}
          </PAText>
          <FormContainer className={styles.postal_code_form}>
            <Form
              layout="vertical"
              className={`shipping-check-form ${styles.input_group}`}
              onValidSubmit={onValidSubmit}
              onValid={() => setValidPostalCode(true)}
              onInvalid={() => setValidPostalCode(false)}
              style={{ width: "100%" }}
            >

              <PostalCodeInput
                defaultValue=""
                addonBefore={<span className="fa fa-map-marker" />}
                buttonAfter={(
                  <PAButton
                    type="submit"
                    className={`${styles.post_code_btn} ${globalStyles.button_radius_left_flat}`}
                    disabled={!validPostalCode}
                  >
                    {t("Check")}
                  </PAButton>
                )}
                inputGroupBtnClass={styles.button_style}
              />
            </Form>
          </FormContainer>
          <HideIf show={isRapidDeliveryAvailable}>
            <div className="text-center">
              <i className="fa fa-check" />
              <PAText bold color="black" fontSize={FONT_SIZE[2]}>Rapid delivery is available here on this zip code.</PAText>
            </div>
          </HideIf>
          <HideIf show={isRapidDeliveryAvailable === false}>
            <div className="text-center">
              <i className="fa fa-times" />
              <PAText bold color="black" fontSize={FONT_SIZE[2]}>Rapid delivery is not available on this zip code.</PAText>
            </div>
          </HideIf>
          <HideIf show={postalCodeError}>
            <PAText bold color="alert-text" fontSize={FONT_SIZE[2]} className="d-flex justify-content-center">{t(postalCodeError)}</PAText>
          </HideIf>
        </div>
      </section>

      <section className="d-flex flex-column flex-wrap m-t-20 p-t-20">
        <PAText bold component="h2" className="text-center p-b-20" color="black">
          {t("How it works")}
        </PAText>

        <DeliverySteps steps={RAPID_DELIVERY_PROCEDURE} />
      </section>

      <section className="d-flex justify-content-center flex-wrap m-t-20 p-t-20 align-items-center">
        <PAText component="h3" bold className="text-center" color="black">
          {t("Frequently Asked Questions")}
        </PAText>

        {faqs && (
          <ContentIdsComponent
            contentIds={[faqs]}
            showSubHeading={false}
            faqColCount={2}
            whiteBackground
            noPadding
          />
        )}
      </section>

      <section className="d-flex flex-column flex-wrap m-t-30 p-t-30">
        <LinkCard
          title={t("Same Day Delivery for Shops and Garages")}
          imageUrl="/cms/pubicImages/images/pa-rapid-delivery.webp"
          subheading={t("samedayDeliveryMessage")}
          link="/in-store-pickup"
          hideButton
          textClassName={styles.centered_text}
        />
      </section>

      <section className="m-t-30 p-t-30 p-b-20">
        <PAText bold component="h2" className="text-center p-b-20" color="black">
          {t("Other ways to get your order")}
        </PAText>

        <div className="d-flex justify-content-between flex-wrap">
          <div className="col-sm-6 d-flex m-b-5 p-lr-0-xs col-xs-12">
            <LinkCard
              title={t("Standard Shipping")}
              imageUrl="/cms/pubicImages/images/pa-standardShipping.webp"
              subheading={t("standardShippingMessage")}
              link="/auto-parts-free-shipping-canada"
            />
          </div>

          <div className="col-sm-6 d-flex m-b-5 p-lr-0-xs col-xs-12">
            <LinkCard
              title={t("In-store pick-up")}
              imageUrl="/cms/pubicImages/images/pa-instore-pickup.webp"
              subheading={t("instorePickUpMessage")}
              link="/in-store-pickup"
            />
          </div>
        </div>
      </section>

      <section className="p-t-20 p-b-20">
        <ChatReturn />
      </section>
    </PageContainer>
  );
}

export default memo(RapidDelivery);
